import app from 'flarum/forum/app';
import Modal from 'flarum/common/components/Modal';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import { formatText, looksLikeUrl } from '../utils/keyText';
function stepAnchorId(section, step) {
  return `key-step-${section}-${step}`;
}

// Some .cle files use a plain row of symbols as a visual section break in
// the original document (e.g. "££ ***********************") - the parser
// has no way to tell that apart from a real title (there's no structural
// difference in the file format, see CleKeyParser.php's own section-marker
// handling), so it comes through as this section's `title` verbatim.
// Showing that as *this section's own heading* is at least honest about
// what the source file contains, but reusing it as a *jump link's label*
// elsewhere reads as broken rather than just odd - a jump link only needs
// something to distinguish one destination from another, and gibberish
// fails at that too. A title with no letters at all (in any script) is
// never real prose, so it's treated as unusable wherever a label is
// needed, though still shown as the section's own heading further down.
function usableTitle(title) {
  return title && /\p{L}/u.test(title) ? title : null;
}

/**
 * Flat, all-at-once rendering of a .cle file's structure (see
 * CleKeyParser.php / DeterminationKeyContentResource) - every section and
 * every step laid out top to bottom on one scrollable page, with each
 * choice that leads to another step or section rendered as a real jump
 * link to that step's own anchor, rather than DeterminationKeyViewer.js's
 * one-step-at-a-time interactive stepper. Matches keys.champis.net's own
 * "AFFICHER" rendering (see that file's own docblock for why the
 * interactive stepper was built as a deliberate improvement on it) -
 * offered here as a second option alongside it, for readers who'd rather
 * skim/search the whole key at once or jump around non-linearly than step
 * through it one choice at a time.
 *
 * Deliberately its own component rather than a "flat mode" bolted onto
 * DeterminationKeyViewer - the two have no shared interaction state (no
 * history stack, no currentSection/currentStep here) and only a handful of
 * lines of loading/error/formatText boilerplate in common, not enough to
 * be worth threading a mode flag through one class for.
 *
 * Jump links scroll their target into view directly (scrollIntoView)
 * rather than being plain `<a href="#...">` left to native browser
 * hash-navigation - avoids leaving a stray #key-step-... fragment in the
 * address bar for what's really an in-modal convenience, not a real page
 * navigation.
 */
export default class DeterminationKeyFlatViewer extends Modal {
  data = null;
  error = false;
  oninit(vnode) {
    super.oninit(vnode);
    this.data = null;
    this.error = false;
    this.load();
  }
  className() {
    return 'Modal--large DeterminationKeyFlatViewer';
  }
  title() {
    return this.data && this.data.title || this.attrs.fileName;
  }
  load() {
    app.request({
      method: 'GET',
      url: app.forum.attribute('apiUrl') + '/determination-key-content',
      params: {
        filter: {
          uuid: this.attrs.uuid
        }
      }
    }).then(response => {
      const item = response.data && response.data[0];
      if (!item) {
        this.error = true;
      } else {
        this.data = item.attributes;
      }
      m.redraw();
    }).catch(() => {
      this.error = true;
      m.redraw();
    });
  }
  content() {
    if (this.error) {
      return m("div", {
        className: "Modal-body"
      }, m("p", {
        className: "DeterminationKeyFlatViewer-error"
      }, app.translator.trans('champis-keys.forum.viewer.error')));
    }
    if (!this.data) {
      return m("div", {
        className: "Modal-body"
      }, m(LoadingIndicator, null));
    }
    return m("div", {
      className: "Modal-body DeterminationKeyFlatViewer"
    }, this.data.sectionOrder.map(sectionKey => this.renderSection(sectionKey)));
  }
  renderSection(sectionKey) {
    const section = this.data.sections[sectionKey];

    // No id on this heading - renderStep() already puts one on step 1's
    // own wrapping div right below (every step gets one, title or not),
    // so a second id here on the exact same anchor target would just be a
    // duplicate DOM id for no reason.
    return m("div", {
      key: sectionKey,
      className: "DeterminationKeyFlatViewer-section"
    }, section.title && m("h3", {
      className: "DeterminationKeyFlatViewer-sectionTitle"
    }, section.title), section.stepOrder.map(stepId => this.renderStep(sectionKey, stepId, section.steps[stepId])));
  }
  renderStep(sectionKey, stepId, choices) {
    return m("div", {
      key: stepId,
      id: stepAnchorId(sectionKey, stepId),
      className: "DeterminationKeyFlatViewer-step"
    }, m("span", {
      className: "DeterminationKeyFlatViewer-stepNumber"
    }, stepId), m("ul", {
      className: "DeterminationKeyFlatViewer-choices"
    }, choices.map((choice, index) => this.renderChoice(sectionKey, choice, index))));
  }
  renderChoice(sectionKey, choice, index) {
    const target = choice.target;
    const isConclusion = target.type === 'conclusion';
    const isInfo = target.type === 'info';
    // Same reasoning as DeterminationKeyViewer.js's own isLink.
    const isLink = target.type === 'link' && (target.external || looksLikeUrl(target.value));
    const isTerminal = isConclusion || isInfo || isLink;
    let jumpTo = null;
    if (target.type === 'step') {
      jumpTo = {
        id: stepAnchorId(sectionKey, target.value),
        label: target.value
      };
    } else if (target.type === 'section') {
      const targetSection = this.data.sections[target.value];
      const firstStep = targetSection && targetSection.stepOrder[0];
      if (firstStep) {
        jumpTo = {
          id: stepAnchorId(target.value, firstStep),
          label: targetSection && usableTitle(targetSection.title) || target.value
        };
      }
    }
    return m("li", {
      key: index,
      className: 'DeterminationKeyFlatViewer-choice' + (isTerminal ? ' DeterminationKeyFlatViewer-choice--conclusion' : '')
    }, m("span", {
      className: "DeterminationKeyFlatViewer-choiceText"
    }, choice.letter && m("strong", null, choice.letter, ". "), formatText(choice.text)), choice.note && m("div", {
      className: "DeterminationKeyFlatViewer-note"
    }, formatText(choice.note)), isConclusion && m("div", {
      className: "DeterminationKeyFlatViewer-result"
    }, m("i", {
      className: "icon fas fa-flag-checkered",
      "aria-hidden": "true"
    }), " ", target.value), isLink && m("a", {
      href: target.value,
      target: "_blank",
      rel: "noopener noreferrer",
      className: "DeterminationKeyFlatViewer-result DeterminationKeyFlatViewer-link"
    }, m("i", {
      className: 'icon fas ' + (target.external ? 'fa-arrow-up-right-from-square' : 'fa-file-arrow-down'),
      "aria-hidden": "true"
    }), ' ', app.translator.trans(target.external ? 'champis-keys.forum.viewer.open_link' : 'champis-keys.forum.viewer.open_file')), jumpTo && m("a", {
      href: '#' + jumpTo.id,
      className: "DeterminationKeyFlatViewer-jump",
      onclick: e => this.jump(e, jumpTo.id)
    }, m("i", {
      className: "icon fas fa-arrow-down",
      "aria-hidden": "true"
    }), ' ', app.translator.trans('champis-keys.forum.viewer.jump_to_step', {
      step: jumpTo.label
    })));
  }
  jump(e, id) {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  }
}
flarum.reg.add('champis-keys', 'forum/components/DeterminationKeyFlatViewer', DeterminationKeyFlatViewer);